import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    ChevronDown, Filter, Loader2, Package, Search, ShoppingBag,
    Sparkles, Tag, Users, X,
} from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { API } from "@/lib/api";
import UpsellBanner from "@/components/upsell/UpsellBanner";
// F13.1 Wave C — marketing hero on top of the live marketplace catalog.
import {
    ProductHero, PrimaryBtn, SecondaryBtn,
} from "@/components/marketing/MarketingPrimitives";


// ------------------------------------------------------------- helpers

function fetchJson(path, opts = {}) {
    // F22 Fix Pass 7F-1 · spread order: opts first, then headers merged
    // last so default headers cannot be clobbered by caller `opts.headers`.
    const url = `${API}${path}`;
    return fetch(url, {
        credentials: "include",
        ...opts,
        headers: { "Accept": "application/json", ...(opts.headers || {}) },
    }).then(async (r) => {
        const t = await r.text();
        let d = null;
        try { d = t ? JSON.parse(t) : null; } catch { d = { raw: t }; }
        if (!r.ok) {
            const err = new Error(d?.detail?.error || d?.detail || d?.message || `HTTP ${r.status}`);
            err.status = r.status; err.data = d; throw err;
        }
        return d;
    });
}

const SORTS = [
    { value: "recent", label: "Newest" },
    { value: "popular", label: "Popular" },
    { value: "price_asc", label: "Price ↑" },
    { value: "price_desc", label: "Price ↓" },
];

const TYPE_LABEL = {
    outreach_template_pack: "Template Pack",
    outreach_sequence: "Sequence",
    lead_list_pack: "Lead List",
    agent_config: "Agent Config",
    badge_cosmetic: "Cosmetic",
    custom_module_ref: "Module Unlock",
    etf_bundle: "ETF Bundle",
    template: "Template",
    module: "Module",
    pack: "Pack",
    service: "Service",
};

function labelFor(t) { return TYPE_LABEL[t] || (t || "").replace(/_/g, " "); }
function fmtCredits(v) { return `${Number(v || 0).toLocaleString()} cr`; }

// FASE F · Marketplace USD-only · USD display with fallback by asset_type.
// Credits remain the unit for operation costs (outreach, content gen, SEO,
// agent deploy) · the marketplace itself is USD-priced going forward.
const ASSET_TYPE_DEFAULT_USD_CENTS = {
    template:   900, agent:   1900, pack:     2900, workflow:  2900,
    service:   4900, token:    900, ecosystem: 9900, universe: 19900,
};
const ASSET_TYPE_IMAGE_URLS = {
    template:  "https://static.prod-images.emergentagent.com/jobs/b61c1e43-89bc-42d2-93ab-3f12f87f040b/images/2596645cd071c40d49945868c676083da73c4d95cd68c6c5f6e5edcb6c55ed49.jpeg",
    agent:     "https://static.prod-images.emergentagent.com/jobs/b61c1e43-89bc-42d2-93ab-3f12f87f040b/images/b2be6cff3ea0fe7b3358f0a9b9ed9291d6eb0ebd2ed354312abd1764fb1f50d2.jpeg",
    pack:      "https://static.prod-images.emergentagent.com/jobs/b61c1e43-89bc-42d2-93ab-3f12f87f040b/images/70e58ea58fa087c3469b923dc1d8185d6ece5e2532d53da5dcbcaae96636071d.jpeg",
    workflow:  "https://static.prod-images.emergentagent.com/jobs/b61c1e43-89bc-42d2-93ab-3f12f87f040b/images/00d843025262e884432e8f781126d74c6c25a72e730bd0b9b23f99e51acbfbd5.jpeg",
    service:   "https://static.prod-images.emergentagent.com/jobs/b61c1e43-89bc-42d2-93ab-3f12f87f040b/images/abba95743b7664dbcf0d4e46dd33f793fb42dc6dc1a4adc798c775d29d138ddb.jpeg",
    token:     "https://static.prod-images.emergentagent.com/jobs/b61c1e43-89bc-42d2-93ab-3f12f87f040b/images/829c0ca12fd60081137d54aebcd56fd46aba287537130b67b6048ed290686b43.jpeg",
    ecosystem: "https://static.prod-images.emergentagent.com/jobs/b61c1e43-89bc-42d2-93ab-3f12f87f040b/images/184848f735da20df32e8931141e0511ce83fa550183e08c9f297e4ff8bc41712.jpeg",
    universe:  "https://static.prod-images.emergentagent.com/jobs/b61c1e43-89bc-42d2-93ab-3f12f87f040b/images/cadf5d6160770618e85b3c61f52aa8e2487146430383dd124a5b833b2d347778.jpeg",
};
function assetUsdCents(item) {
    const explicit = Number(item?.price_usd_cents || 0);
    if (explicit > 0) return explicit;
    return ASSET_TYPE_DEFAULT_USD_CENTS[item?.asset_type] || ASSET_TYPE_DEFAULT_USD_CENTS.template;
}
function fmtUsd(cents) {
    const n = Number(cents || 0) / 100;
    return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}
function assetImageUrl(item) {
    return item?.image_url || item?.thumbnail_url
        || ASSET_TYPE_IMAGE_URLS[item?.asset_type]
        || ASSET_TYPE_IMAGE_URLS.template;
}


// ------------------------------------------------------------- filter sidebar

function FilterSidebar({
    categories, assetTypes, creators,
    selCats, setSelCats, selTypes, setSelTypes,
    priceMin, setPriceMin, priceMax, setPriceMax,
    selCreator, setSelCreator,
    onReset,
}) {
    const toggle = (arr, val, setter) => {
        setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
    };
    return (
        <aside className="w-full md:w-64 shrink-0 space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
                    <Filter className="h-3 w-3 text-[var(--color-secondary)]" /> Filters
                </div>
                <button
                    onClick={onReset}
                    className="text-[10px] text-text-secondary hover:text-text-main"
                >
                    Reset
                </button>
            </div>

            <FilterGroup icon={Tag} label="Category">
                {categories.length === 0 && <FilterEmpty />}
                {categories.map((c) => (
                    <FilterCheckbox
                        key={c} checked={selCats.includes(c)}
                        onChange={() => toggle(selCats, c, setSelCats)}
                        label={c}
                    />
                ))}
            </FilterGroup>

            <FilterGroup icon={Package} label="Asset type">
                {assetTypes.length === 0 && <FilterEmpty />}
                {assetTypes.map((t) => (
                    <FilterCheckbox
                        key={t} checked={selTypes.includes(t)}
                        onChange={() => toggle(selTypes, t, setSelTypes)}
                        label={labelFor(t)}
                    />
                ))}
            </FilterGroup>

            <FilterGroup icon={Sparkles} label="Price range (USD)">
                <div className="flex items-center gap-2">
                    <input
                        type="number" min={0} value={priceMin ?? ""}
                        onChange={(e) => setPriceMin(e.target.value ? Number(e.target.value) : null)}
                        placeholder="Min"
                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-xs text-text-main"
                    />
                    <span className="text-text-secondary text-xs">–</span>
                    <input
                        type="number" min={0} value={priceMax ?? ""}
                        onChange={(e) => setPriceMax(e.target.value ? Number(e.target.value) : null)}
                        placeholder="Max"
                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-xs text-text-main"
                    />
                </div>
            </FilterGroup>

            <FilterGroup icon={Users} label="Creator">
                <select
                    value={selCreator || ""}
                    onChange={(e) => setSelCreator(e.target.value || null)}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-xs text-text-main"
                >
                    <option value="">All creators</option>
                    {creators.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name} ({c.assets_count})
                        </option>
                    ))}
                </select>
            </FilterGroup>
        </aside>
    );
}

function FilterGroup({ icon: Icon, label, children }) {
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
                <Icon className="h-3 w-3" /> {label}
            </div>
            <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">{children}</div>
        </div>
    );
}

function FilterEmpty() {
    return <p className="text-[10px] text-text-secondary italic">Loading…</p>;
}

function FilterCheckbox({ checked, onChange, label }) {
    return (
        <label className="flex items-center gap-2 text-xs text-text-main cursor-pointer hover:text-[var(--color-secondary)]">
            <input
                type="checkbox" checked={checked} onChange={onChange}
                className="rounded border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
            />
            <span className="capitalize">{label}</span>
        </label>
    );
}


// ------------------------------------------------------------- asset card

function AssetCard({ item, onOpen }) {
    const imageUrl = assetImageUrl(item);
    const usdLabel = fmtUsd(assetUsdCents(item));
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="group rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden hover:border-[#00A3FF]/60 transition-colors cursor-pointer"
            onClick={() => onOpen(item)}
            data-testid={`mp-asset-card-${item.slug || item.id}`}
        >
            <div className="relative aspect-video w-full bg-[var(--color-bg)] flex items-center justify-center overflow-hidden">
                <img
                    src={imageUrl}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const sib = e.currentTarget.nextElementSibling;
                        if (sib) sib.style.display = "flex";
                    }}
                />
                <div className="hidden absolute inset-0 items-center justify-center">
                    <Package className="h-10 w-10 text-text-secondary/40" />
                </div>
                {item.asset_source === "phase16c" && (
                    <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-black/60 text-white border border-white/20 backdrop-blur">
                        Creator
                    </span>
                )}
                {item.creator_verified && (
                    <span
                        data-testid={`mp-verified-badge-${item.slug}`}
                        className="absolute top-2 left-2 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--color-success)]/20 text-[var(--color-success)] border border-[var(--color-success)]/40 flex items-center gap-1"
                    >
                        ✓ Verified
                    </span>
                )}
            </div>
            <div className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-medium text-text-main leading-snug line-clamp-2 group-hover:text-[#00A3FF]">
                        {item.title}
                    </h3>
                    <span
                        className="text-sm font-semibold text-[#00A3FF] tabular-nums shrink-0"
                        data-testid={`mp-asset-price-${item.slug || item.id}`}
                    >
                        {usdLabel}
                    </span>
                </div>
                <p className="text-xs text-text-secondary line-clamp-2">{item.description}</p>
                <div className="flex items-center justify-between text-[10px] text-text-secondary">
                    <span className="uppercase tracking-widest">{labelFor(item.asset_type)}</span>
                    {item.creator_name && (
                        <span className="truncate max-w-[100px]">by {item.creator_name}</span>
                    )}
                </div>
            </div>
        </motion.div>
    );
}


// ------------------------------------------------------------- detail modal

function AssetDetailModal({ item, onClose }) {
    if (!item) return null;
    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />
            <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            >
                <div className="pointer-events-auto w-full max-w-lg rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                    <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                        <div>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
                                <ShoppingBag className="h-3 w-3 text-[var(--color-secondary)]" />
                                {item.asset_source === "phase16c" ? "Creator asset" : "Curated"}
                                {" · "}
                                {labelFor(item.asset_type)}
                            </div>
                            <h2 className="text-lg font-semibold">{item.title}</h2>
                            {item.creator_name && (
                                <p className="text-[10px] text-text-secondary mt-1">by {item.creator_name}</p>
                            )}
                        </div>
                        <button onClick={onClose} className="text-text-secondary hover:text-text-main">
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="p-5 overflow-y-auto flex-1 space-y-3">
                        <p className="text-sm text-text-secondary whitespace-pre-line">{item.description}</p>
                        <div className="flex flex-wrap items-center gap-2">
                            {(item.tags || []).map((t) => (
                                <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-text-secondary border border-[var(--color-border)]">
                                    {t}
                                </span>
                            ))}
                        </div>
                        {item.asset_source === "phase16c" && item.source_metadata?.current_version && (
                            <p className="text-[10px] text-text-secondary">Version: v{item.source_metadata.current_version}</p>
                        )}
                    </div>
                    <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]/40">
                        <span
                            className="text-base font-semibold text-[#00A3FF] tabular-nums"
                            data-testid={`mp-asset-detail-price-${item.slug || item.id}`}
                        >
                            {fmtUsd(assetUsdCents(item))}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onClose}
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5"
                            >
                                Close
                            </button>
                            <button
                                data-testid={`mp-asset-buy-${item.slug || item.id}`}
                                onClick={async () => {
                                    try {
                                        const r = await fetchJson(
                                            `/v2/marketplace/assets/${encodeURIComponent(item.id || item.slug)}/checkout/usd`,
                                            { method: "POST", body: JSON.stringify({}) },
                                        );
                                        if (r?.checkout_url) {
                                            window.location.href = r.checkout_url;
                                        } else {
                                            toast.error("Checkout unavailable");
                                        }
                                    } catch (e) {
                                        toast.error(e.message || "Checkout failed");
                                    }
                                }}
                                className="text-xs rounded-full px-4 py-1.5 font-medium bg-[#00A3FF] hover:bg-[#0088D4] text-white"
                            >
                                Buy for {fmtUsd(assetUsdCents(item))}
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}


// ------------------------------------------------------------- page

export default function Marketplace() {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [phaseCount, setPhaseCount] = useState({ p5: 0, p16c: 0 });
    const [error, setError] = useState(null);
    const [disabled, setDisabled] = useState(false);
    const [detail, setDetail] = useState(null);

    // Filter state (4 standard filters only)
    const [categories, setCategories] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);
    const [creators, setCreators] = useState([]);
    const [selCats, setSelCats] = useState([]);
    const [selTypes, setSelTypes] = useState([]);
    const [priceMin, setPriceMin] = useState(null);
    const [priceMax, setPriceMax] = useState(null);
    const [selCreator, setSelCreator] = useState(null);
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState("recent");
    const [offset, setOffset] = useState(0);
    const PAGE_SIZE = 24;

    // Load filter facets once
    useEffect(() => {
        Promise.all([
            fetchJson("/marketplace/categories"),
            fetchJson("/marketplace/asset-types"),
            fetchJson("/marketplace/creators?limit=30"),
        ]).then(([c, t, cr]) => {
            setCategories(c?.items || []);
            setAssetTypes(t?.items || []);
            setCreators(cr?.items || []);
        }).catch((e) => {
            if (e.status === 503) setDisabled(true);
        });
    }, []);

    const load = useCallback(async () => {
        const p = new URLSearchParams();
        // NOTE: unified endpoint takes single category/asset_type today —
        // when the user selects multiple, we send the first and rely on
        // client-side filtering for extra ones below.
        if (selCats[0]) p.set("category", selCats[0]);
        if (selTypes[0]) p.set("asset_type", selTypes[0]);
        if (priceMin !== null) p.set("price_min", priceMin);
        if (priceMax !== null) p.set("price_max", priceMax);
        if (selCreator) p.set("creator_id", selCreator);
        if (query.trim()) p.set("q", query.trim());
        p.set("sort", sort);
        p.set("limit", PAGE_SIZE);
        p.set("offset", offset);
        try {
            const r = await fetchJson(`/marketplace/browse?${p.toString()}`);
            let it = r?.items || [];
            // client-side extra category/type filtering when multiple selected
            if (selCats.length > 1) it = it.filter((x) => selCats.includes(x.category));
            if (selTypes.length > 1) it = it.filter((x) => selTypes.includes(x.asset_type));
            setItems(it);
            setTotal(r?.total || 0);
            setPhaseCount({ p5: r?.phase5_count || 0, p16c: r?.phase16c_count || 0 });
            setError(null);
            setDisabled(false);
        } catch (e) {
            if (e.status === 503) setDisabled(true);
            else setError(e.message);
        }
    }, [selCats, selTypes, priceMin, priceMax, selCreator, query, sort, offset]);

    useEffect(() => {
        (async () => { setLoading(true); await load(); setLoading(false); })();
    }, [load]);

    const resetFilters = () => {
        setSelCats([]); setSelTypes([]); setPriceMin(null); setPriceMax(null);
        setSelCreator(null); setQuery(""); setOffset(0);
    };

    const activeFilterCount = useMemo(
        () => selCats.length + selTypes.length + (priceMin ? 1 : 0)
            + (priceMax ? 1 : 0) + (selCreator ? 1 : 0),
        [selCats, selTypes, priceMin, priceMax, selCreator],
    );

    return (
        <SidebarLayout>
            {/* F13.1 Wave C · marketing hero above the live catalog */}
            <ProductHero
                eyebrow="MARKETPLACE"
                title="Buy, sell, and discover AI assets"
                subtitle="Templates, agents, sequences, datasets — the asset layer for autonomous lead generation."
                primaryCTA={<PrimaryBtn to="/register" testid="marketplace-marketing-cta-register">Explore marketplace</PrimaryBtn>}
                secondaryCTA={<SecondaryBtn href="#marketplace-catalog" testid="marketplace-marketing-cta-catalog">Learn how it works</SecondaryBtn>}
                testid="marketplace-marketing-hero"
            />
            <div id="marketplace-catalog" className="max-w-[1280px] w-full mx-auto p-6 md:p-8 space-y-6">
                <UpsellBanner surface="marketplace" />
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
                            <ShoppingBag className="h-3 w-3 text-[var(--color-secondary)]" />
                            Marketplace
                        </div>
                        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
                            Marketplace
                        </h1>
                        <p className="text-xs text-text-secondary mt-1 max-w-xl">
                            Curated packs and community creator assets in one unified
                            catalog. <span className="text-text-main">{total}</span> assets ·{" "}
                            {phaseCount.p5} curated · {phaseCount.p16c} community.
                        </p>
                    </div>
                    <div className="relative w-full md:w-80">
                        <Search className="h-4 w-4 text-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text" value={query}
                            onChange={(e) => { setQuery(e.target.value); setOffset(0); }}
                            placeholder="Search assets…"
                            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] pl-10 pr-4 py-2 text-sm text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-[var(--color-primary)]"
                        />
                    </div>
                </div>

                {disabled && (
                    <div className="rounded-2xl border border-neutral-400/30 bg-neutral-400/5 p-6 flex items-start gap-3">
                        <div>
                            <h3 className="text-sm font-semibold text-neutral-400">Marketplace consolidation is disabled</h3>
                            <p className="text-xs text-text-secondary mt-1">
                                The unified marketplace is currently paused. The legacy marketplace is still accessible directly.
                            </p>
                        </div>
                    </div>
                )}

                <div className="flex flex-col md:flex-row gap-6">
                    <FilterSidebar
                        categories={categories}
                        assetTypes={assetTypes}
                        creators={creators}
                        selCats={selCats} setSelCats={(v) => { setSelCats(v); setOffset(0); }}
                        selTypes={selTypes} setSelTypes={(v) => { setSelTypes(v); setOffset(0); }}
                        priceMin={priceMin} setPriceMin={(v) => { setPriceMin(v); setOffset(0); }}
                        priceMax={priceMax} setPriceMax={(v) => { setPriceMax(v); setOffset(0); }}
                        selCreator={selCreator} setSelCreator={(v) => { setSelCreator(v); setOffset(0); }}
                        onReset={resetFilters}
                    />

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-xs text-text-secondary">
                                {items.length} of {total} results{activeFilterCount ? ` · ${activeFilterCount} filter(s) active` : ""}
                            </p>
                            <div className="relative">
                                <select
                                    value={sort}
                                    onChange={(e) => { setSort(e.target.value); setOffset(0); }}
                                    className="text-xs rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] pl-3 pr-8 py-1.5 text-text-main focus:outline-none appearance-none"
                                >
                                    {SORTS.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                                <ChevronDown className="h-3 w-3 text-text-secondary absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                        </div>

                        {loading ? (
                            <div className="p-16 flex items-center justify-center text-text-secondary text-xs gap-2">
                                <Loader2 className="h-3 w-3 animate-spin" /> Loading marketplace…
                            </div>
                        ) : error ? (
                            <div className="rounded-2xl border border-[var(--color-alert)]/30 bg-[var(--color-alert)]/5 p-6 text-sm text-[var(--color-alert)]">
                                {error}
                            </div>
                        ) : items.length === 0 ? (
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-16 text-center text-sm text-text-secondary">
                                No assets match your filters. Try widening the price range or clearing filters.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {items.map((it) => (
                                    <AssetCard key={`${it.asset_source}-${it.id}`} item={it} onOpen={setDetail} />
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {total > PAGE_SIZE && (
                            <div className="mt-6 flex items-center justify-between">
                                <button
                                    disabled={offset === 0}
                                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                    className="text-xs rounded-full border border-[var(--color-border)] px-3 py-1.5 text-text-secondary hover:text-text-main disabled:opacity-40"
                                >
                                    ← Prev
                                </button>
                                <span className="text-xs text-text-secondary tabular-nums">
                                    {Math.floor(offset / PAGE_SIZE) + 1} / {Math.ceil(total / PAGE_SIZE)}
                                </span>
                                <button
                                    disabled={offset + PAGE_SIZE >= total}
                                    onClick={() => setOffset(offset + PAGE_SIZE)}
                                    className="text-xs rounded-full border border-[var(--color-border)] px-3 py-1.5 text-text-secondary hover:text-text-main disabled:opacity-40"
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AssetDetailModal item={detail} onClose={() => setDetail(null)} />
        </SidebarLayout>
    );
}
